/*
  Zentriertes Popup für Schichten und Absenzen im Schichtplan V2.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Popper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
// Dasselbe Symbol wie in /settings fuer "Abteilungen & Schichten".
import IconDepartment from "@mui/icons-material/CorporateFareOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AppButton from "../../../../design-system/components/AppButton";
import AppPersonBadge from "../../../../design-system/components/AppPersonBadge";
import {
  IconAdd,
  IconChevronDown,
  IconClose,
  IconDelete,
  IconEdit,
  IconHoliday,
  IconSearch,
  IconTime,
} from "../../../../design-system/icons";
import { formatTimestampLabel } from "../../domain/date/timestampLabels";
import { formatTime, toIsoDate } from "../../domain/date/month";
import { getAvailabilitySummaryForRange } from "../../domain/model/availability";
import { clampShiftEndToStartDay } from "../../domain/model/conflicts";
import { CUSTOM_DEPARTMENT_OPTION_LABEL } from "../../domain/model/departments";

const ENTRY_TYPES = Object.freeze({
  SHIFT: "shift",
  ABSENCE: "absence",
});
const ABSENCE_OPTION_SOURCES = Object.freeze({
  ABSENCE: "absence",
  USER_HOLIDAY: "user_holiday",
});

const EMPTY_OPTION_VALUE = "__empty__";
const CUSTOM_SHIFT_TEMPLATE_ID = "__custom_shift__";
const CURRENT_SHIFT_TEMPLATE_ID = "__current_shift__";
const CUSTOM_SHIFT_OPTION = Object.freeze({
  id: CUSTOM_SHIFT_TEMPLATE_ID,
  name: "+ Individuelle Schicht",
  isCustom: true,
});
const SHIFT_RANGE_KEYS = Object.freeze({
  startDate: "startDate",
  endDate: "endDate",
  startTime: "startTime",
  endTime: "endTime",
});
const ABSENCE_RANGE_KEYS = Object.freeze({
  startDate: "absenceStartDate",
  endDate: "absenceEndDate",
  startTime: "absenceStartTime",
  endTime: "absenceEndTime",
});
const EDITOR_SELECT_MENU_PROPS = {
  sx: { zIndex: 3400 },
  PaperProps: {
    className: "spv2-editor-select-menu",
  },
};
const EMPLOYEE_POPPER_MODIFIERS = [
  {
    name: "offset",
    options: {
      offset: [0, 7],
    },
  },
  {
    name: "preventOverflow",
    options: {
      boundary: "viewport",
      padding: 12,
    },
  },
];
/*
  Ausklapplisten dürfen über den Dialog hinausragen – so zeigen sie mehr
  Einträge auf einmal, statt an der Fusszeile abzuschneiden. Ihre Grenze ist
  darum das Fenster, nicht der Dialog. Gemessen wird die grössere der beiden
  Seiten, weil die Listen nach oben kippen, wenn unten zu wenig Platz ist.
*/
const EDITOR_MENU_MAX_HEIGHT = 420;
const EDITOR_MENU_MIN_HEIGHT = 160;
const EDITOR_MENU_VIEWPORT_INSET = 16;
const MILLISECONDS_PER_DAY = 86_400_000;
const MINUTES_PER_DAY = 1440;
/* Untertitel des Kopfes: ein Tag ausgeschrieben, eine Spanne kurz. */
const SUBTITLE_DAY_FORMATTER = new Intl.DateTimeFormat("de-CH", {
  weekday: "long",
  day: "2-digit",
  month: "long",
  year: "numeric",
});
const SUBTITLE_RANGE_FORMATTER = new Intl.DateTimeFormat("de-CH", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function asText(value) {
  return String(value || "").trim();
}

function parseIsoDateKey(value) {
  const match = asText(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/*
  Untertitel im Dialogkopf: der Tag, um den es geht, dazu die Abteilung. Über
  mehrere Tage wird daraus eine Spanne – ein Wochentag sagt dann nichts mehr aus.
*/
function buildHeaderSubtitle({ startDate, endDate, department }) {
  const start = parseIsoDateKey(startDate);
  if (!start) return "";

  const end = parseIsoDateKey(endDate);
  const dateLabel =
    end && asText(endDate) !== asText(startDate)
      ? `${SUBTITLE_RANGE_FORMATTER.format(start)} – ${SUBTITLE_RANGE_FORMATTER.format(end)}`
      : SUBTITLE_DAY_FORMATTER.format(start);

  return [dateLabel, asText(department)].filter(Boolean).join(" · ");
}

function timeToMinutes(value) {
  const match = asText(value).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/* Tagesnummer über UTC, damit die Zeitumstellung nicht mitrechnet. */
function isoDateToDayIndex(value) {
  const match = asText(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day)) / MILLISECONDS_PER_DAY;
}

function getBreakMinutes(breaks) {
  return (Array.isArray(breaks) ? breaks : []).reduce((total, item) => {
    // Dauer-Pausen (aus den Schicht-Stammdaten) haben keine Lage im Tag,
    // zaehlen aber genauso von der bezahlten Zeit weg.
    if (item?.type === "duration") {
      const minutes = Number(item?.durationMinutes);
      return Number.isFinite(minutes) && minutes > 0 ? total + Math.round(minutes) : total;
    }
    const start = timeToMinutes(item?.startTime);
    const end = timeToMinutes(item?.endTime);
    if (start === null || end === null || end <= start) return total;
    return total + (end - start);
  }, 0);
}

/* Bezahlte Dauer der Schicht: Ende minus Beginn über Tagesgrenzen, Pausen ab. */
function getShiftDurationMinutes(draft) {
  const startDay = isoDateToDayIndex(draft?.startDate);
  const endDay = isoDateToDayIndex(draft?.endDate);
  const startMinutes = timeToMinutes(draft?.startTime);
  const endMinutes = timeToMinutes(draft?.endTime);
  if (startDay === null || endDay === null || startMinutes === null || endMinutes === null) return 0;

  const span = (endDay - startDay) * MINUTES_PER_DAY + endMinutes - startMinutes;
  return Math.max(0, span - getBreakMinutes(draft?.breaks));
}

function formatDurationLabel(totalMinutes) {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes} Min`;
  if (!minutes) return `${hours} Std`;
  return `${hours} Std ${minutes} Min`;
}

function getCreatedTimestamp(source) {
  return asText(source?.created);
}

function getReleasedTimestamp(source) {
  return asText(source?.released);
}

function normalizeDepartmentName(item) {
  if (Array.isArray(item)) return asText(item[1] ?? item[0]);
  if (item && typeof item === "object") {
    return asText(item.name ?? item.label ?? item.department ?? item.title ?? item.id);
  }
  return asText(item);
}

function normalizeDepartmentShortName(item) {
  if (Array.isArray(item)) return asText(item[2]);
  if (item && typeof item === "object") {
    return asText(item.shortName ?? item.shorthand_symbol ?? item.short_name);
  }
  return "";
}

function normalizeDepartmentId(item, fallbackName) {
  if (Array.isArray(item)) return asText(item[0]) || fallbackName;
  if (item && typeof item === "object") return asText(item.id ?? item.DepartmentID ?? item.departmentId) || fallbackName;
  return fallbackName;
}

function safeOptionColor(value) {
  const color = asText(value);
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color) ? color : "";
}

function normalizeDepartmentColor(item) {
  if (Array.isArray(item)) return safeOptionColor(item[3]);
  if (item && typeof item === "object") {
    return safeOptionColor(item.color ?? item.color_hex ?? item.colorHex);
  }
  return "";
}

function formatNameWithShort(name, shortName) {
  const cleanName = asText(name);
  const cleanShortName = asText(shortName);
  if (!cleanName || !cleanShortName || cleanName.toLocaleLowerCase("de") === cleanShortName.toLocaleLowerCase("de")) {
    return cleanName;
  }
  return `${cleanName} (${cleanShortName})`;
}

function formatTemplateOptionLabel(template) {
  if (!template) return "";
  if (typeof template === "string") return template;
  if (template.isCustom) return template.name;
  return [formatNameWithShort(template.name, template.shortName), template.timeLabel].filter(Boolean).join(" · ");
}

function uniqueByName(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = item.name.toLocaleLowerCase("de");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function uniqueById(items) {
  const seen = new Set();
  return items.filter((item) => {
    const source = item.source || "item";
    const idKey = item.key ? `key:${item.key}` : item.id ? `${source}:id:${item.id}` : "";
    const nameKey = `${source}:name:${item.name.toLocaleLowerCase("de")}`;
    if (!item.name || (idKey && seen.has(idKey)) || seen.has(nameKey)) return false;
    if (idKey) seen.add(idKey);
    seen.add(nameKey);
    return true;
  });
}

function ColoredOptionLabel({ color, primary, secondary }) {
  const optionColor = safeOptionColor(color);
  const optionStyle = optionColor ? { "--spv2-editor-option-color": optionColor } : undefined;

  return (
    <span className={`spv2-editor-colored-option${optionColor ? " has-color" : ""}`} style={optionStyle}>
      {optionColor ? <span className="spv2-editor-color-dot" aria-hidden="true" /> : null}
      <span className="spv2-editor-colored-copy">
        <span className="spv2-editor-colored-primary">{primary}</span>
        {secondary ? <span className="spv2-editor-colored-secondary">{secondary}</span> : null}
      </span>
    </span>
  );
}

/*
  Stundenwerte im Entwurf sind Text, damit ein leeres Feld auch leer bleibt
  und nicht als 0 verstanden wird.
*/
function hoursToField(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  // 0 ist ein gültiger Wert ("zählt null Stunden") und darf nicht als
  // "kein Wert gesetzt" durchrutschen.
  return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : "";
}

function fieldToHours(value) {
  const raw = asText(value).replace(",", ".");
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 24) return null;
  return Math.round(parsed * 100) / 100;
}

function formatHoursValue(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  const rounded = Math.round(parsed * 100) / 100;
  return `${String(rounded).replace(".", ",")} h`;
}

/*
  Arbeitstage im gewählten Zeitraum. Nur damit sichtbar wird, was am Ende
  gebucht wird – gerechnet wird verbindlich im Backend.
*/
function countWorkingDaysInRange(startDate, endDate, workingDaysPerWeek) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate) || start;
  if (!start || !end || end < start) return 0;

  const limit = Math.max(1, Math.min(7, Number(workingDaysPerWeek) || 5));
  let count = 0;
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    // Montag = 0 … Sonntag = 6, damit "5 Arbeitstage" Mo–Fr bedeutet.
    const weekday = (cursor.getDay() + 6) % 7;
    if (weekday < limit) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

function parseIsoDate(value) {
  const raw = asText(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = new Date(`${raw}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeAbsenceOption(option) {
  if (!option) return null;
  const id = asText(option.id ?? option.AbsenceID ?? option.absenceId);
  const name = asText(option.name ?? option.label ?? option.title);
  if (!name) return null;

  const rawHours = option.hoursPerDay ?? option.hours_per_day;

  return {
    key: `${ABSENCE_OPTION_SOURCES.ABSENCE}:${id || name}`,
    id: id || name,
    name,
    source: ABSENCE_OPTION_SOURCES.ABSENCE,
    color: asText(option.color ?? option.color_hex ?? option.colorHex),
    // null heisst "kein fester Wert" – dann gilt das individuelle Tagessoll.
    hoursPerDay: rawHours == null || rawHours === "" ? null : Number(rawHours),
    countsAsWorkTime: option.countsAsWorkTime ?? option.counts_as_work_time ?? true,
  };
}

function normalizeHolidayTypeOption(option) {
  if (!option) return null;
  const id = asText(option.id ?? option.UserHolidayTypeID ?? option.userHolidayTypeId);
  const name = asText(option.name ?? option.label ?? option.title);
  if (!id || !name) return null;

  return {
    key: `${ABSENCE_OPTION_SOURCES.USER_HOLIDAY}:${id}`,
    id,
    name,
    source: ABSENCE_OPTION_SOURCES.USER_HOLIDAY,
    hoursPerDay: Number(option.hoursPerDay ?? option.hours_per_day ?? 0) || 0,
    countsAsWorkTime: true,
  };
}

function absenceDraftPatch(option) {
  const source = option?.source || ABSENCE_OPTION_SOURCES.ABSENCE;

  const patch = {
    absenceOptionKey: option?.key || "",
    absenceKind: source,
    absenceId: source === ABSENCE_OPTION_SOURCES.ABSENCE ? option?.id || "" : "",
    userHolidayTypeId: source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY ? option?.id || "" : "",
    absenceType: option?.name || "Absenz",
    // Beim Wechsel der Art gilt wieder deren Vorgabe – ein abweichender Wert
    // gehörte zur vorherigen Art.
    hoursPerDay: hoursToField(option?.hoursPerDay),
  };

  if (source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY) {
    patch.absenceAllDay = true;
  }

  return patch;
}

function normalizeAbsenceOptions(absenceOptions, userHolidayTypes, context) {
  const contextOption = context?.absenceOption
    ? context.absenceOption.source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY
      ? normalizeHolidayTypeOption(context.absenceOption)
      : normalizeAbsenceOption(context.absenceOption)
    : null;
  const options = [
    contextOption,
    ...(Array.isArray(userHolidayTypes) ? userHolidayTypes : []).map(normalizeHolidayTypeOption).filter(Boolean),
    ...(Array.isArray(absenceOptions) ? absenceOptions : []).map(normalizeAbsenceOption).filter(Boolean),
  ].filter(Boolean);
  const activeShift = context?.shift;
  if (activeShift?.entryType !== ENTRY_TYPES.ABSENCE) return uniqueById(options);

  const activeOption = {
    key: `${ABSENCE_OPTION_SOURCES.ABSENCE}:${asText(activeShift.absenceId) || asText(activeShift.title) || "Absenz"}`,
    id: asText(activeShift.absenceId) || asText(activeShift.title) || "Absenz",
    name: asText(activeShift.title || activeShift.department) || "Absenz",
    source: ABSENCE_OPTION_SOURCES.ABSENCE,
    color: asText(activeShift.color),
  };

  return uniqueById(asText(activeShift.absenceId) ? [activeOption, ...options] : [...options, activeOption]);
}

function normalizeDepartments(departments, context) {
  const companyDepartments = (Array.isArray(departments) ? departments : [])
    .map((item) => {
      const name = normalizeDepartmentName(item);
      return name
        ? {
            id: normalizeDepartmentId(item, name),
            name,
            shortName: normalizeDepartmentShortName(item),
            color: normalizeDepartmentColor(item),
          }
        : null;
    })
    .filter(Boolean);
  /*
    Die Abteilung des bearbeiteten Eintrags wird ergänzt, falls sie in den
    Stammdaten fehlt — sonst stünde sie nicht in der Liste, aus der sie stammt.
    Eine individuelle Abteilung ist davon ausgenommen: sie lebt im Textfeld und
    sähe hier aus wie ein gepflegter Eintrag, den man wieder auswählen kann.
    Auch die Sammelzeile "Individuell" ist kein Abteilungsname.
  */
  const contextDepartment = asText(context?.shift?.customDepartmentName)
    ? ""
    : asText(context?.shift?.department) ||
      (context?.rowMode === "department" && !context?.row?.isCustomDepartment
        ? asText(context?.row?.name)
        : "");
  const contextDepartmentOption = companyDepartments.find(
    (department) => department.name.toLocaleLowerCase("de") === contextDepartment.toLocaleLowerCase("de")
  );

  return uniqueByName([
    ...companyDepartments,
    ...(contextDepartment
      ? [
          {
            id: contextDepartmentOption?.id || contextDepartment,
            name: contextDepartment,
            shortName: contextDepartmentOption?.shortName || "",
            color: contextDepartmentOption?.color || "",
          },
        ]
      : []),
  ]).sort((left, right) => left.name.localeCompare(right.name, "de", { sensitivity: "base" }));
}

function normalizeEmployee(employee) {
  if (!employee) return null;
  const id = asText(employee.id ?? employee.employeeId ?? employee.userId);
  if (!id) return null;
  const name =
    asText(employee.name) ||
    `${asText(employee.firstName)} ${asText(employee.lastName)}`.trim() ||
    asText(employee.email) ||
    `Mitarbeitende ${id}`;

  return {
    id,
    name,
    email: asText(employee.email),
    isPlaceholder: Boolean(employee.isPlaceholder) || Number(employee.numericId) < 0,
  };
}

function getContextEmployee(context) {
  const shiftEmployeeId = asText(context?.shift?.employeeId);
  if (shiftEmployeeId) {
    return normalizeEmployee({
      id: shiftEmployeeId,
      name: context?.shift?.employeeName,
    });
  }

  if (context?.rowMode !== "employee") return null;
  return normalizeEmployee(context?.row);
}

function normalizeEmployees(employees, context) {
  const contextEmployee = getContextEmployee(context);
  const normalizedEmployees = (Array.isArray(employees) ? employees : []).map(normalizeEmployee).filter(Boolean);
  const seen = new Set();

  return [...normalizedEmployees, contextEmployee].filter((employee) => {
    if (!employee || employee.isPlaceholder || seen.has(employee.id)) return false;
    seen.add(employee.id);
    return true;
  });
}

function parseTimeRange(value) {
  const match = asText(value).match(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/);
  if (!match) return { startTime: "", endTime: "" };
  return {
    startTime: match[1].padStart(5, "0"),
    endTime: match[2].padStart(5, "0"),
  };
}

function getShiftTime(shift, field, fallback = "") {
  const formatted = formatTime(shift?.[field]);
  if (formatted) return formatted;
  const range = parseTimeRange(shift?.timeLabel);
  return range[field === "start" ? "startTime" : "endTime"] || fallback;
}

function normalizeEditorBreaks(breaks) {
  return (Array.isArray(breaks) ? breaks : [])
    .map((item, index) => {
      if (asText(item?.type).toLowerCase() === "duration") {
        const minutes = Number(item?.durationMinutes);
        if (!Number.isFinite(minutes) || minutes <= 0) return null;
        return {
          id: asText(item?.id) || `break-${index}`,
          type: "duration",
          startTime: "",
          endTime: "",
          durationMinutes: Math.round(minutes),
        };
      }

      const startTime = item?.startTime || formatTime(item?.start) || "12:00";
      const endTime = item?.endTime || formatTime(item?.end) || "12:30";
      return {
        id: asText(item?.id) || `break-${index}`,
        type: "fixed",
        startTime,
        endTime: isTimeBefore(endTime, startTime) ? startTime : endTime,
        durationMinutes: null,
      };
    })
    .filter((item) => item && (item.type === "duration" || (item.startTime && item.endTime)));
}

/*
  Stammdaten-Pausen der gewaehlten Vorlage als Editor-Pausen: feste Fenster
  erscheinen wie manuell erfasste Pausen, Dauer-Pausen als Minutenzeile. Beim
  Speichern werden sie ganz normale Pausen des Eintrags.
*/
function templateBreaksToDraft(template) {
  return normalizeEditorBreaks(
    (Array.isArray(template?.breaks) ? template.breaks : []).map((item, index) => ({
      ...item,
      id: `template-break-${index}`,
    }))
  );
}

function isDateBefore(left, right) {
  if (!left || !right) return false;
  return String(left) < String(right);
}

function isSameDate(left, right) {
  if (!left || !right) return false;
  return String(left) === String(right);
}

function isTimeBefore(left, right) {
  if (!left || !right) return false;
  return String(left) < String(right);
}

function normalizeTimeInputValue(value) {
  const text = asText(value).replace(/\./g, ":");
  if (!text) return "";

  let hourText = "";
  let minuteText = "";

  if (/^\d{1,2}:\d{0,2}$/.test(text)) {
    const [hours, minutes = ""] = text.split(":");
    hourText = hours;
    minuteText = minutes || "00";
  } else if (/^\d{1,4}$/.test(text)) {
    hourText = text.length <= 2 ? text : text.slice(0, -2);
    minuteText = text.length <= 2 ? "00" : text.slice(-2);
  } else {
    return "";
  }

  const hours = Number(hourText);
  const minutes = Number(minuteText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return "";
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return "";

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function sanitizeTimeInputValue(value) {
  return asText(value).replace(/[^\d:.]/g, "").replace(/\./g, ":").slice(0, 5);
}

function addDaysToIsoDate(dateKey, amount) {
  if (!dateKey) return "";
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;

  date.setDate(date.getDate() + amount);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getTemplateEndDate(startDate, startTime, endTime) {
  return isTimeBefore(endTime, startTime) ? addDaysToIsoDate(startDate, 1) : startDate;
}

function buildCreatedMeta(entry, isEditMode) {
  const formatted = formatTimestampLabel(getCreatedTimestamp(entry));
  const fallback = isEditMode ? "unbekannt" : "Noch nicht gespeichert";

  return {
    label: "Erstellt:",
    value: formatted || fallback,
  };
}

function buildReleaseMeta(shift) {
  const formatted = formatTimestampLabel(getReleasedTimestamp(shift));
  return {
    label: "Freigegeben:",
    value: formatted || "-",
  };
}

function departmentKey(value) {
  return asText(value).toLocaleLowerCase("de");
}

function templateMatchesDepartment(template, department) {
  const selectedDepartment = departmentKey(department);
  if (!selectedDepartment) return true;
  return departmentKey(template?.department) === selectedDepartment;
}

function isCustomTemplateId(value) {
  return asText(value) === CUSTOM_SHIFT_TEMPLATE_ID;
}

function normalizeDateTimeRange(draft, keys, patch = {}) {
  const next = { ...draft, ...patch };
  const startDate = next[keys.startDate];
  const endDate = next[keys.endDate];
  const startTime = next[keys.startTime];
  const endTime = next[keys.endTime];

  if (isDateBefore(endDate, startDate)) {
    next[keys.endDate] = startDate;
  }

  if (isSameDate(next[keys.startDate], next[keys.endDate]) && isTimeBefore(endTime, startTime)) {
    next[keys.endTime] = startTime;
  }

  return next;
}

function buildDateTimeValue(dateValue, timeValue, fallbackTime) {
  const dateText = asText(dateValue);
  if (!dateText) return "";

  const timeText = asText(timeValue) || fallbackTime;
  if (!timeText) return dateText;

  return `${dateText}T${timeText.length === 5 ? `${timeText}:00` : timeText}`;
}

function getInitialShiftDepartment({ isCustomShift, initialDepartment, activeTemplate, firstDepartment }) {
  if (isCustomShift) return initialDepartment;
  return initialDepartment || activeTemplate?.department || firstDepartment;
}

function buildInitialDraft(context, departmentOptions, shiftTemplates, employeeOptions, absenceOptions) {
  const shift = context?.shift || null;
  const userHoliday = context?.userHoliday || null;
  const isUserHolidayEdit = Boolean(userHoliday);
  const isAbsenceShift = context?.entryType === ENTRY_TYPES.ABSENCE || shift?.entryType === ENTRY_TYPES.ABSENCE || isUserHolidayEdit;
  const firstAbsence = absenceOptions[0] || { id: "", name: "Absenz" };
  const contextAbsence = context?.absenceOption
    ? absenceOptions.find(
        (option) =>
          option.source === (context.absenceOption.source || ABSENCE_OPTION_SOURCES.ABSENCE) &&
          asText(option.id) === asText(context.absenceOption.id)
      )
    : null;
  const activeAbsence =
    contextAbsence ||
    (isUserHolidayEdit
      ? absenceOptions.find(
          (option) =>
            option.source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY &&
            asText(option.id) === asText(userHoliday.holidayTypeId)
        )
      : null) ||
    absenceOptions.find((option) => asText(option.id) === asText(shift?.absenceId)) ||
    absenceOptions.find(
      (option) => option.name.toLocaleLowerCase("de") === asText(shift?.title || shift?.department).toLocaleLowerCase("de")
    ) ||
    firstAbsence;
  const firstDepartment = departmentOptions[0]?.name || "";
  const firstTemplate = shiftTemplates[0] || null;
  const rowEmployeeId = context?.rowMode === "employee" ? asText(context?.row?.id) : "";
  const initialEmployeeId = asText(userHoliday?.userId) || asText(shift?.employeeId) || rowEmployeeId || employeeOptions[0]?.id || "";
  const initialEmployee =
    employeeOptions.find((employee) => employee.id === initialEmployeeId) ||
    normalizeEmployee({ id: initialEmployeeId, name: shift?.employeeName || context?.row?.name });
  const isCustomShift = Boolean(shift && !isAbsenceShift && !shift.shiftId);
  const shiftTemplateId = shift?.shiftId ? String(shift.shiftId) : isCustomShift ? CUSTOM_SHIFT_TEMPLATE_ID : "";
  const templateFromShift = shiftTemplates.find((template) => String(template.id) === shiftTemplateId);
  /*
    Die Sammelzeile "Individuell" ist kein Abteilungsname — aus ihr heraus
    angelegte Einträge starten ohne Abteilung, nicht mit dem Wort "Individuell".
  */
  const rowDepartment =
    context?.rowMode === "department" && !context?.row?.isUnassigned && !context?.row?.isCustomDepartment
      ? asText(context?.row?.name)
      : "";
  const customDepartment = asText(shift?.customDepartmentName);
  const initialDepartment = customDepartment
    ? customDepartment
    : isCustomShift
      ? shift?.department || rowDepartment || ""
      : shift?.department || rowDepartment || templateFromShift?.department || firstTemplate?.department || firstDepartment;
  const activeTemplate =
    templateFromShift ||
    shiftTemplates.find((template) => templateMatchesDepartment(template, initialDepartment)) ||
    firstTemplate;
  const activeTemplateTimes = parseTimeRange(activeTemplate?.timeLabel);
  const rangeStartDate = asText(context?.timeOffRange?.startDate);
  const rangeEndDate = asText(context?.timeOffRange?.endDate);
  const fallbackDate = userHoliday?.startDate || shift?.dateKey || rangeStartDate || context?.day?.key || "";
  const startDate = userHoliday?.startDate || toIsoDate(shift?.start) || rangeStartDate || fallbackDate;
  const startTime = getShiftTime(shift, "start", activeTemplateTimes.startTime || "08:00");
  const endTime = getShiftTime(shift, "end", activeTemplateTimes.endTime || "17:00");
  const rawEndDate = userHoliday?.endDate || toIsoDate(shift?.end) || rangeEndDate || startDate;
  const endDate =
    isSameDate(startDate, rawEndDate) && isTimeBefore(endTime, startTime)
      ? addDaysToIsoDate(startDate, 1)
      : rawEndDate;

  return normalizeDateTimeRange({
    entryType: isAbsenceShift ? ENTRY_TYPES.ABSENCE : ENTRY_TYPES.SHIFT,
    startDate,
    endDate,
    employeeId: initialEmployee?.id || "",
    employeeName: initialEmployee?.name || "",
    department: customDepartment
      ? customDepartment
      : getInitialShiftDepartment({ isCustomShift, initialDepartment, activeTemplate, firstDepartment }),
    isCustomDepartment: Boolean(customDepartment),
    templateId: shiftTemplateId || activeTemplate?.id || CUSTOM_SHIFT_TEMPLATE_ID,
    title: isCustomShift ? shift?.title || "" : shift?.title || activeTemplate?.name || "",
    startTime,
    endTime,
    note: shift?.note || "",
    // Beim Bearbeiten zaehlen die Pausen des Eintrags (auch wenn sie geleert
    // wurden); nur beim Anlegen kommen die Stammdaten-Pausen der Vorlage mit.
    breaks: shift ? normalizeEditorBreaks(shift?.breaks) : templateBreaksToDraft(activeTemplate),
    ...absenceDraftPatch(isAbsenceShift ? activeAbsence : firstAbsence),
    absenceAllDay: isAbsenceShift
      ? activeAbsence.source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY || Boolean(shift?.isAllDay)
      : true,
    absenceStartDate: startDate,
    absenceEndDate: endDate,
    absenceStartTime: isAbsenceShift ? startTime : "08:00",
    absenceEndTime: isAbsenceShift ? endTime : "17:00",
    absenceNote: isAbsenceShift ? shift?.note || "" : "",
    /*
      Beim Bearbeiten zählt der im Eintrag festgehaltene Wert, nicht der des
      Typs: der Typ kann sich seither geändert haben. Nur beim Anlegen kommt
      die Vorgabe der Art zum Zug (aus absenceDraftPatch oben).
    */
    ...(isAbsenceShift && (userHoliday || shift)
      ? {
          hoursPerDay: hoursToField(
            userHoliday ? userHoliday.hoursPerDay : shift?.absenceHoursPerDay
          ) || hoursToField(activeAbsence?.hoursPerDay),
        }
      : {}),
  }, SHIFT_RANGE_KEYS);
}

function SegmentButton({ active, icon, children, onClick }) {
  return (
    <button type="button" className={`spv2-editor-segment${active ? " is-active" : ""}`} onClick={onClick}>
      {icon}
      <span>{children}</span>
    </button>
  );
}

function Field({ label, icon, children, span = 1, className = "" }) {
  return (
    <Box className={`spv2-editor-field${span === 2 ? " span-2" : ""}${className ? ` ${className}` : ""}`}>
      {label ? (
        <label>
          {icon}
          <span>{label}</span>
        </label>
      ) : null}
      {children}
    </Box>
  );
}

function AbsenceSourceBadge({ source, className }) {
  const isUserHoliday = source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY;
  const Icon = isUserHoliday ? IconHoliday : EventBusyOutlinedIcon;

  return (
    <span
      className={`${className}${isUserHoliday ? " is-user-holiday" : " is-absence"}`}
      aria-label={isUserHoliday ? "Ferien" : "Absenz"}
    >
      <Icon fontSize="inherit" />
    </span>
  );
}

function FooterMetaRow({ meta }) {
  if (!meta) return null;

  return (
    <Typography component="div" className="spv2-editor-footer-meta-row">
      <span className="spv2-editor-footer-meta-label">{meta.label}</span>
      <span className="spv2-editor-footer-meta-value">{meta.value}</span>
    </Typography>
  );
}

function getEditorMenuMaxHeight(anchorEl) {
  if (!anchorEl?.getBoundingClientRect) return EDITOR_MENU_MAX_HEIGHT;

  const triggerRect = anchorEl.getBoundingClientRect();
  const spaceBelow = Math.floor(window.innerHeight - triggerRect.bottom - EDITOR_MENU_VIEWPORT_INSET);
  const spaceAbove = Math.floor(triggerRect.top - EDITOR_MENU_VIEWPORT_INSET);
  const availableHeight = Math.max(spaceBelow, spaceAbove);

  return Math.max(EDITOR_MENU_MIN_HEIGHT, Math.min(EDITOR_MENU_MAX_HEIGHT, availableHeight));
}

/*
  `measure` gibt es, weil das MUI-Select-Menü seine Position beim Öffnen einmal
  berechnet: die Höhe muss dann schon stimmen, sonst rückt das Menü nachträglich
  vom Auslöser weg. Der Effekt hält sie danach nur noch aktuell.
*/
function useEditorMenuMaxHeight(anchorEl, isOpen) {
  const [maxHeight, setMaxHeight] = useState(EDITOR_MENU_MAX_HEIGHT);

  const measure = useCallback((element) => {
    const nextMaxHeight = getEditorMenuMaxHeight(element);
    setMaxHeight(nextMaxHeight);
    return nextMaxHeight;
  }, []);

  useEffect(() => {
    if (!isOpen || !anchorEl) return undefined;

    const updateMaxHeight = () => measure(anchorEl);
    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    const frameHandle = requestFrame(updateMaxHeight);

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);

    return () => {
      cancelFrame(frameHandle);
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [anchorEl, isOpen, measure]);

  return [maxHeight, measure];
}

function EditorSelect({ value, disabled = false, ariaLabel, children, renderValue, onChange }) {
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuMaxHeight, measureMenuMaxHeight] = useEditorMenuMaxHeight(anchorRef.current, isOpen);
  const menuProps = {
    ...EDITOR_SELECT_MENU_PROPS,
    PaperProps: {
      ...EDITOR_SELECT_MENU_PROPS.PaperProps,
      style: { maxHeight: menuMaxHeight },
    },
  };

  const openMenu = () => {
    measureMenuMaxHeight(anchorRef.current);
    setIsOpen(true);
  };

  return (
    <FormControl size="small" fullWidth ref={anchorRef}>
      <Select
        displayEmpty
        className="spv2-editor-select"
        value={value}
        open={isOpen}
        disabled={disabled}
        IconComponent={IconChevronDown}
        inputProps={{ "aria-label": ariaLabel }}
        MenuProps={menuProps}
        renderValue={renderValue}
        onOpen={openMenu}
        onClose={() => setIsOpen(false)}
        onChange={onChange}
      >
        {children}
      </Select>
    </FormControl>
  );
}

/*
  Derselbe Mitarbeitenden-Badge wie im Raster, in der Statistik, auf /team und
  auf /availability: neutrale Variante, Initialen kommen aus dem DS.
*/
function EmployeeAvatar({ name }) {
  return (
    <AppPersonBadge name={asText(name)} size="sm" />
  );
}

function getEmployeeAvailabilityLabel(summary) {
  if (summary?.status === "partial") {
    return summary?.kind === "absence" ? "Teilweise abwesend" : "Teilweise verfügbar";
  }

  if (summary?.status === "available") return "Verfügbar";
  if (summary?.status === "unavailable") {
    return summary?.kind === "absence" ? "Abwesend" : "Nicht verfügbar";
  }

  return "Verfügbarkeit unbekannt";
}

function getEmployeeAvailabilityInfo(summary) {
  return summary?.tooltip || "Keine Verfügbarkeitsdaten";
}

function EmployeeAvailabilityDot({ summary }) {
  const status = summary?.status || "unknown";

  return (
    <span
      className={`spv2-editor-availability-dot is-${status}`}
      aria-label={getEmployeeAvailabilityLabel(summary)}
      title={summary?.tooltip || ""}
    />
  );
}

function EmployeeSelect({ employees, value, disabled, showAvailability = true, availabilityByEmployeeId, onChange }) {
  const [query, setQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const searchInputRef = useRef(null);
  const selectedId = value?.id || "";
  const isOpen = Boolean(anchorEl) && !disabled;
  const [menuMaxHeight] = useEditorMenuMaxHeight(anchorEl, isOpen);
  const menuWidth = anchorEl ? Math.round(anchorEl.getBoundingClientRect().width) : 0;
  const normalizedQuery = query.trim().toLocaleLowerCase("de");
  const filteredEmployees = useMemo(
    () =>
      employees.filter((employee) => {
        if (!normalizedQuery) return true;
        return employee.name.toLocaleLowerCase("de").includes(normalizedQuery);
      }),
    [employees, normalizedQuery]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    const focusHandle = requestFrame(() => {
      const searchInput = searchInputRef.current;
      if (!searchInput) return;
      if (document.activeElement !== searchInput) searchInput.focus();
      if (!query) searchInput.select?.();
    });

    return () => cancelFrame(focusHandle);
  }, [isOpen, query]);

  const openEmployeeMenu = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const closeEmployeeMenu = () => {
    setAnchorEl(null);
    setQuery("");
  };

  const handleEmployeeSelect = (employee) => {
    onChange(employee);
    closeEmployeeMenu();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeEmployeeMenu();
      return;
    }

    event.stopPropagation();
  };

  const renderValue = (employeeId) => {
    const employee = employees.find((item) => String(item.id) === String(employeeId));
    if (!employee) {
      return <span className="spv2-editor-select-placeholder">Mitarbeitende wählen</span>;
    }

    const summary = showAvailability ? availabilityByEmployeeId?.[employee.id] : null;
    const availabilityInfo = showAvailability ? getEmployeeAvailabilityInfo(summary) : "";

    return (
      <span className="spv2-editor-employee-select-value">
        <EmployeeAvatar name={employee.name} />
        <span className="spv2-editor-employee-option-name">{employee.name}</span>
        {showAvailability ? (
          // Im Wert eine Pille mit dem Punkt vorne; in der Liste unten bleiben
          // Text und Punkt getrennt, damit die Punkte eine Spalte bilden.
          <span className="spv2-editor-employee-status">
            <EmployeeAvailabilityDot summary={summary} />
            <span className="spv2-editor-employee-status-text" title={availabilityInfo}>
              {availabilityInfo}
            </span>
          </span>
        ) : null}
      </span>
    );
  };

  return (
    <FormControl size="small" fullWidth>
      <ClickAwayListener onClickAway={closeEmployeeMenu}>
        <Box className="spv2-editor-employee-control">
          <button
            type="button"
            role="combobox"
            aria-label="Mitarbeitende"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls="spv2-editor-employee-listbox"
            className={`spv2-editor-select spv2-editor-employee-trigger${isOpen ? " is-open" : ""}`}
            disabled={disabled}
            onClick={(event) => {
              if (isOpen) {
                closeEmployeeMenu();
                return;
              }
              openEmployeeMenu(event);
            }}
          >
            {renderValue(selectedId)}
            <IconChevronDown className="spv2-editor-employee-trigger-icon" />
          </button>
          {/* Ohne Portal würde die Fläche des Dialogs (overflow: hidden) die
              Liste am Rand abschneiden – sie soll aber darüber hinausragen. */}
          <Popper
            open={isOpen}
            anchorEl={anchorEl}
            placement="bottom-start"
            className="spv2-editor-employee-popper"
            modifiers={EMPLOYEE_POPPER_MODIFIERS}
          >
            <Box
              className="spv2-editor-select-menu spv2-editor-employee-menu"
              style={{
                ...(menuWidth ? { width: menuWidth } : {}),
                "--spv2-editor-menu-max-height": `${menuMaxHeight}px`,
              }}
            >
              <Box className="spv2-editor-employee-menu-content">
                <Box className="spv2-editor-employee-search-row">
                  <TextField
                    size="small"
                    className="spv2-editor-employee-search"
                    placeholder="Mitarbeitende suchen"
                    value={query}
                    inputRef={searchInputRef}
                    inputProps={{ "aria-label": "Mitarbeitende suchen" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconSearch fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) => setQuery(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onKeyDown={handleSearchKeyDown}
                  />
                </Box>
                <Box
                  id="spv2-editor-employee-listbox"
                  className="spv2-editor-employee-menu-list"
                  role="listbox"
                  aria-label="Mitarbeitende"
                >
                  {!filteredEmployees.length ? (
                    <span className="spv2-editor-employee-empty-option">Keine Mitarbeitenden</span>
                  ) : null}
                  {filteredEmployees.map((employee) => {
                    const summary = showAvailability ? availabilityByEmployeeId?.[employee.id] : null;
                    const availabilityInfo = showAvailability ? getEmployeeAvailabilityInfo(summary) : "";
                    const isSelected = String(employee.id) === String(selectedId);

                    return (
                      <button
                        key={employee.id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className="spv2-editor-employee-option"
                        onClick={() => handleEmployeeSelect(employee)}
                      >
                        <span className="spv2-editor-employee-option-main">
                          <EmployeeAvatar name={employee.name} />
                          <span className="spv2-editor-employee-option-copy">
                            <span className="spv2-editor-employee-option-name">{employee.name}</span>
                          </span>
                          {showAvailability ? (
                            <span className="spv2-editor-employee-availability-end">
                              <span className="spv2-editor-employee-availability-info" title={availabilityInfo}>
                                {availabilityInfo}
                              </span>
                              <EmployeeAvailabilityDot summary={summary} />
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Popper>
        </Box>
      </ClickAwayListener>
    </FormControl>
  );
}

function ShiftTemplateSelect({ templates, value, inputValue, onChange, onInputChange }) {
  // "+ Individuelle Schicht" steht am Ende: die gepflegten Vorlagen sind der
  // Regelfall und sollen zuerst im Blick sein. Gleiche Reihenfolge wie im
  // Abteilungsfeld darunter.
  const options = [...templates, CUSTOM_SHIFT_OPTION];
  const isCustomValue = Boolean(value?.isCustom);
  const selectedColor = !isCustomValue ? safeOptionColor(value?.color) : "";
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(0);
  const [menuMaxHeight] = useEditorMenuMaxHeight(anchorRef.current, isOpen);
  const selectedTemplateLabel = value && !isCustomValue ? formatNameWithShort(value.name, value.shortName) : "";
  const selectedTemplateTime = value && !isCustomValue ? value.timeLabel : "";
  const selectedTriggerLabel = [selectedTemplateLabel, selectedTemplateTime].filter(Boolean).join(" · ");

  const openMenu = () => {
    setMenuWidth(anchorRef.current?.offsetWidth || 0);
    setIsOpen(true);
  };
  const closeMenu = () => setIsOpen(false);
  const toggleMenu = () => {
    if (isOpen) {
      closeMenu();
      return;
    }
    openMenu();
  };
  const selectTemplate = (option) => {
    onChange(option);
    closeMenu();
    if (option?.isCustom) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };
  const handleTriggerKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      openMenu();
      return;
    }
    if (event.key === "Escape") {
      closeMenu();
    }
  };

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <Box className="spv2-editor-template-select" ref={anchorRef}>
        {isCustomValue ? (
          <Box className={`spv2-editor-template-custom-control${isOpen ? " is-open" : ""}`}>
            <input
              ref={inputRef}
              className="spv2-editor-template-custom-input"
              value={inputValue}
              placeholder="Individuelle Schicht eingeben"
              aria-label="Individuelle Schicht eingeben"
              onChange={(event) => onInputChange(event.target.value, "input")}
              onKeyDown={(event) => {
                if (event.key === "Escape") closeMenu();
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  openMenu();
                }
              }}
            />
            <button
              type="button"
              className="spv2-editor-template-trigger-button"
              aria-label={isOpen ? "Schichtauswahl schließen" : "Schichtauswahl öffnen"}
              aria-expanded={isOpen}
              onClick={toggleMenu}
              onKeyDown={handleTriggerKeyDown}
            >
              <IconChevronDown className="spv2-editor-template-trigger-icon" />
            </button>
          </Box>
        ) : (
          <button
            type="button"
            className={`spv2-editor-template-trigger${isOpen ? " is-open" : ""}`}
            aria-label="Schicht wählen"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            onClick={toggleMenu}
            onKeyDown={handleTriggerKeyDown}
          >
            {value ? (
              <ColoredOptionLabel color={selectedColor} primary={selectedTriggerLabel} />
            ) : (
              <span className="spv2-editor-select-placeholder">Schicht wählen</span>
            )}
            <IconChevronDown className="spv2-editor-template-trigger-icon" />
          </button>
        )}

        {/* Portal aus demselben Grund wie bei der Mitarbeitenden-Liste. */}
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          className="spv2-editor-template-popper"
          modifiers={EMPLOYEE_POPPER_MODIFIERS}
        >
          <Box
            className="spv2-editor-select-menu spv2-editor-template-menu"
            role="listbox"
            aria-label="Schichtauswahl"
            style={{
              ...(menuWidth ? { width: menuWidth } : {}),
              "--spv2-editor-menu-max-height": `${menuMaxHeight}px`,
            }}
          >
            {options.map((option) => {
              const isSelected = String(option.id) === String(value?.id);
              const optionLabel = option.isCustom ? option.name : formatNameWithShort(option.name, option.shortName);

              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`spv2-editor-template-option${option.isCustom ? " is-custom" : ""}${
                    isSelected ? " is-selected" : ""
                  }`}
                  onClick={() => selectTemplate(option)}
                >
                  {option.isCustom ? (
                    <span className="spv2-editor-template-option-name">{optionLabel}</span>
                  ) : (
                    <ColoredOptionLabel color={option.color} primary={optionLabel} secondary={option.timeLabel} />
                  )}
                </button>
              );
            })}
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

/*
  Abteilungsfeld — dieselbe Bedienung wie das Schichtfeld darüber: Liste zum
  Aufklappen, und für eine individuelle Abteilung ein Textfeld an derselben
  Stelle. Bereits verwendete individuelle Namen werden bewusst nicht angeboten;
  die Liste zeigt nur Stammdaten, alles andere wird jedes Mal eingetippt.
*/
function DepartmentSelect({ departments, value, isCustom, onSelect, onInputChange }) {
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(0);
  const [menuMaxHeight] = useEditorMenuMaxHeight(anchorRef.current, isOpen);
  const selectedDepartment = !isCustom
    ? departments.find((department) => department.name === value) || null
    : null;

  const openMenu = () => {
    setMenuWidth(anchorRef.current?.offsetWidth || 0);
    setIsOpen(true);
  };
  const closeMenu = () => setIsOpen(false);
  const toggleMenu = () => {
    if (isOpen) {
      closeMenu();
      return;
    }
    openMenu();
  };
  const choose = (option) => {
    onSelect(option);
    closeMenu();
    if (option.kind === "new-custom") {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };
  const handleTriggerKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      openMenu();
      return;
    }
    if (event.key === "Escape") closeMenu();
  };

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <Box className="spv2-editor-template-select" ref={anchorRef}>
        {isCustom ? (
          <Box className={`spv2-editor-template-custom-control${isOpen ? " is-open" : ""}`}>
            <input
              ref={inputRef}
              className="spv2-editor-template-custom-input"
              value={value}
              placeholder="Individuelle Abteilung eingeben"
              aria-label="Individuelle Abteilung eingeben"
              onChange={(event) => onInputChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") closeMenu();
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  openMenu();
                }
              }}
            />
            <button
              type="button"
              className="spv2-editor-template-trigger-button"
              aria-label={isOpen ? "Abteilungsauswahl schliessen" : "Abteilungsauswahl öffnen"}
              aria-expanded={isOpen}
              onClick={toggleMenu}
              onKeyDown={handleTriggerKeyDown}
            >
              <IconChevronDown className="spv2-editor-template-trigger-icon" />
            </button>
          </Box>
        ) : (
          <button
            type="button"
            className={`spv2-editor-template-trigger${isOpen ? " is-open" : ""}`}
            aria-label="Abteilung"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            onClick={toggleMenu}
            onKeyDown={handleTriggerKeyDown}
          >
            {selectedDepartment ? (
              <ColoredOptionLabel
                color={selectedDepartment.color}
                primary={formatNameWithShort(selectedDepartment.name, selectedDepartment.shortName)}
              />
            ) : (
              /*
                Platzhalter wie im Schichtfeld daneben. Er zeigt Eintraege ohne
                Abteilung an — anlegen laesst sich ein solcher nur noch, indem
                man eine individuelle Abteilung ohne Namen laesst.
              */
              <span className="spv2-editor-select-placeholder">Abteilung wählen</span>
            )}
            <IconChevronDown className="spv2-editor-template-trigger-icon" />
          </button>
        )}

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          className="spv2-editor-template-popper"
          modifiers={EMPLOYEE_POPPER_MODIFIERS}
        >
          <Box
            className="spv2-editor-select-menu spv2-editor-template-menu"
            role="listbox"
            aria-label="Abteilungsauswahl"
            style={{
              ...(menuWidth ? { width: menuWidth } : {}),
              "--spv2-editor-menu-max-height": `${menuMaxHeight}px`,
            }}
          >
            {departments.map((department) => {
              const isSelected = !isCustom && department.name === value;
              return (
                <button
                  key={department.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`spv2-editor-template-option${isSelected ? " is-selected" : ""}`}
                  onClick={() => choose({ kind: "master", name: department.name })}
                >
                  <ColoredOptionLabel
                    color={department.color}
                    primary={formatNameWithShort(department.name, department.shortName)}
                  />
                </button>
              );
            })}

            {/*
              Reihenfolge wie im Schichtfeld: "+ Individuell" steht am Ende, denn
              die Stammabteilungen sind der Regelfall und sollen zuerst im Blick
              sein.
            */}
            <button
              type="button"
              role="option"
              aria-selected={isCustom && !value}
              className="spv2-editor-template-option is-custom"
              onClick={() => choose({ kind: "new-custom" })}
            >
              <span className="spv2-editor-template-option-name">{CUSTOM_DEPARTMENT_OPTION_LABEL}</span>
            </button>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

function TimeTextInput({
  value,
  min,
  ariaLabel,
  className = "",
  wrapperClassName = "",
  showIcon = true,
  onCommit,
}) {
  const [draftValue, setDraftValue] = useState(value || "");
  const [isEditing, setIsEditing] = useState(false);
  // Zweites, unsichtbares input[type=time]: liefert die Uhr des Browsers, ohne
  // dass das Textfeld seine Tipp-Logik (HH:MM, Enter, Escape) verliert.
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!isEditing) setDraftValue(value || "");
  }, [isEditing, value]);

  const resetDraft = () => {
    setDraftValue(value || "");
    setIsEditing(false);
  };

  const commitDraft = () => {
    const normalizedValue = normalizeTimeInputValue(draftValue);
    if (!normalizedValue) {
      resetDraft();
      return;
    }

    const nextValue = min && isTimeBefore(normalizedValue, min) ? min : normalizedValue;
    setDraftValue(nextValue);
    setIsEditing(false);
    if (nextValue !== value) onCommit?.(nextValue);
  };

  return (
    <div className={`spv2-editor-time-input${wrapperClassName ? ` ${wrapperClassName}` : ""}`}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck="false"
        className={className}
        value={isEditing ? draftValue : value || ""}
        aria-label={ariaLabel}
        placeholder="HH:MM"
        maxLength={5}
        onFocus={() => {
          setDraftValue(value || "");
          setIsEditing(true);
        }}
        onChange={(event) => setDraftValue(sanitizeTimeInputValue(event.target.value))}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitDraft();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            resetDraft();
          }
        }}
      />
      {showIcon ? (
        <>
          <input
            ref={pickerRef}
            type="time"
            className="spv2-editor-time-picker"
            tabIndex={-1}
            aria-hidden="true"
            value={value || ""}
            min={min || undefined}
            onChange={(event) => {
              const nextValue = normalizeTimeInputValue(event.target.value);
              if (!nextValue) return;
              const guardedValue = min && isTimeBefore(nextValue, min) ? min : nextValue;
              setDraftValue(guardedValue);
              if (guardedValue !== value) onCommit?.(guardedValue);
            }}
          />
          <button
            type="button"
            className="spv2-editor-time-input-icon"
            aria-label={`${ariaLabel || "Uhrzeit"} auswählen`}
            onClick={() => {
              const picker = pickerRef.current;
              if (!picker) return;
              if (typeof picker.showPicker === "function") {
                picker.showPicker();
                return;
              }
              picker.focus();
              picker.click();
            }}
          >
            <IconTime fontSize="small" />
          </button>
        </>
      ) : null}
    </div>
  );
}

function DateTimeField({
  label,
  dateValue,
  timeValue,
  minDate,
  minTime,
  dateAriaLabel,
  timeAriaLabel,
  onDateChange,
  onTimeCommit,
}) {
  return (
    <Field label={label} icon={<IconTime fontSize="inherit" />}>
      <Box className="spv2-editor-date-time-control">
        <input
          type="date"
          className="spv2-editor-control spv2-editor-date-input"
          value={dateValue}
          min={minDate}
          aria-label={dateAriaLabel}
          onChange={onDateChange}
        />
        <TimeTextInput
          className="spv2-editor-control spv2-editor-time-control"
          value={timeValue}
          min={minTime}
          ariaLabel={timeAriaLabel}
          showIcon
          onCommit={onTimeCommit}
        />
      </Box>
    </Field>
  );
}

function TimeField({ label, value, min, ariaLabel, onCommit }) {
  return (
    <Field label={label} icon={<IconTime fontSize="inherit" />}>
      <TimeTextInput
        className="spv2-editor-control"
        value={value}
        min={min}
        ariaLabel={ariaLabel}
        onCommit={onCommit}
      />
    </Field>
  );
}

/*
  Stunden pro Tag für Absenzen und Ferien.

  Das Feld ist immer sichtbar und aus dem Typ vorbelegt. Darunter steht, woher
  der Wert kommt; weicht er ab, wird daraus ein Hinweis mit Zurücksetzen. Sind
  Uhrzeiten erfasst, gewinnt die Uhrzeit — dann zeigt das Feld die daraus
  folgende Dauer und ist gesperrt.
*/
function AbsenceHoursField({
  span = 1,
  value,
  typeName,
  typeHours,
  countsAsWorkTime,
  isAllDay,
  startTime,
  endTime,
  workingDayCount,
  onChange,
  onReset,
}) {
  if (!countsAsWorkTime) {
    return (
      <Field label="Stunden/Tag" icon={<IconTime fontSize="inherit" />} span={span}>
        <input className="spv2-editor-control" value="" disabled aria-label="Stunden pro Tag" readOnly />
        <span className="spv2-editor-hours-hint">
          „{typeName}“ zählt nicht als Arbeitszeit — dieser Eintrag rechnet null Stunden.
        </span>
      </Field>
    );
  }

  if (!isAllDay) {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const durationHours =
      startMinutes != null && endMinutes != null && endMinutes > startMinutes
        ? (endMinutes - startMinutes) / 60
        : 0;

    return (
      <Field label="Stunden/Tag" icon={<IconTime fontSize="inherit" />} span={span}>
        <input
          className="spv2-editor-control"
          value={durationHours ? formatHoursValue(durationHours) : ""}
          disabled
          readOnly
          aria-label="Stunden pro Tag"
        />
        <span className="spv2-editor-hours-hint">Ergibt sich aus der Uhrzeit</span>
      </Field>
    );
  }

  const currentHours = fieldToHours(value);
  const defaultHours = typeHours == null ? null : Number(typeHours);
  const isCustom = currentHours !== defaultHours;
  const totalHours = currentHours == null ? null : currentHours * Math.max(0, workingDayCount);

  return (
    <Field label="Stunden/Tag" icon={<IconTime fontSize="inherit" />} span={span}>
      <input
        type="number"
        className="spv2-editor-control"
        value={value ?? ""}
        min={0}
        max={24}
        step={0.1}
        placeholder={defaultHours == null ? "Tagessoll" : String(defaultHours)}
        aria-label="Stunden pro Tag"
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="spv2-editor-hours-hint">
        {isCustom ? (
          <>
            <span className="spv2-editor-hours-custom">Abweichend</span>
            <button type="button" className="spv2-editor-hours-reset" onClick={onReset}>
              Zurücksetzen
            </button>
          </>
        ) : defaultHours == null ? (
          <span>Kein fester Wert — es zählt das Tagessoll der Person</span>
        ) : (
          <span>Standard aus „{typeName}“</span>
        )}
        {totalHours != null && workingDayCount > 1 ? (
          <span className="spv2-editor-hours-total">
            {workingDayCount} Arbeitstage × {formatHoursValue(currentHours)} = {formatHoursValue(totalHours)}
          </span>
        ) : null}
      </span>
    </Field>
  );
}

export default function ShiftEditorPopup({
  open,
  context,
  employees = [],
  departments = [],
  shiftTemplates = [],
  absenceOptions = [],
  userHolidayTypes = [],
  availabilityEntries = [],
  availabilityDefaults = [],
  workingDaysPerWeek = 5,
  companyAvailabilityMode = "absence",
  userAvailabilityModes = {},
  onClose,
  onSubmit,
  onDelete,
}) {
  const departmentOptions = useMemo(() => normalizeDepartments(departments, context), [departments, context]);
  const employeeOptions = useMemo(() => normalizeEmployees(employees, context), [employees, context]);
  const normalizedAbsenceOptions = useMemo(
    () => normalizeAbsenceOptions(absenceOptions, userHolidayTypes, context),
    [absenceOptions, context, userHolidayTypes]
  );
  const templateOptions = useMemo(() => {
    const normalizedTemplates = Array.isArray(shiftTemplates) ? shiftTemplates : [];
    const activeShift = context?.shift;
    const activeTemplateExists = normalizedTemplates.some((template) => String(template.id) === String(activeShift?.shiftId));
    const currentShiftTemplate = activeShift
      ? {
          id: activeShift.shiftId ? String(activeShift.shiftId) : CURRENT_SHIFT_TEMPLATE_ID,
          name: activeShift.title || "Aktuelle Schicht",
          shortName: activeShift.titleShort || "",
          timeLabel: activeShift.timeLabel || "",
          department: activeShift.department || "",
          departmentShortName: activeShift.departmentShortName || "",
          color: activeShift.color || "",
        }
      : null;

    if (!activeShift || !activeShift.shiftId) return normalizedTemplates;
    if (activeTemplateExists) return normalizedTemplates;

    return [currentShiftTemplate, ...normalizedTemplates];
  }, [context, shiftTemplates]);
  const [draft, setDraft] = useState(() =>
    buildInitialDraft(context, departmentOptions, templateOptions, employeeOptions, normalizedAbsenceOptions)
  );

  useEffect(() => {
    if (!open) return;
    setDraft(buildInitialDraft(context, departmentOptions, templateOptions, employeeOptions, normalizedAbsenceOptions));
  }, [context, departmentOptions, employeeOptions, normalizedAbsenceOptions, open, templateOptions]);

  const isEditMode = context?.action === "edit";
  const isAbsence = draft.entryType === ENTRY_TYPES.ABSENCE;
  /*
    Vorlagen hängen an echten Abteilungen. Zu einer individuellen passt keine —
    darum entfällt der Filter dort, statt eine leere Liste zu zeigen: eine
    Vorlage bringt Zeiten und Farbe mit, auch wenn die Abteilung individuell ist.
  */
  const filteredTemplateOptions = useMemo(
    () =>
      draft.isCustomDepartment
        ? templateOptions
        : templateOptions.filter((template) => templateMatchesDepartment(template, draft.department)),
    [draft.department, draft.isCustomDepartment, templateOptions]
  );
  const usesCustomShift = !isAbsence && isCustomTemplateId(draft.templateId);
  const selectedTemplate = filteredTemplateOptions.find((template) => String(template.id) === String(draft.templateId));
  const selectedTemplateValue = usesCustomShift ? CUSTOM_SHIFT_OPTION : selectedTemplate || null;
  const templateInputValue = usesCustomShift ? draft.title || "" : selectedTemplate ? formatTemplateOptionLabel(selectedTemplate) : "";
  const selectedEmployee = employeeOptions.find((employee) => String(employee.id) === String(draft.employeeId)) || null;
  const showEmployeeAvailability = isAbsence
    ? isSameDate(draft.absenceStartDate, draft.absenceEndDate)
    : isSameDate(draft.startDate, draft.endDate);
  const availabilityRange = useMemo(() => {
    if (isAbsence) {
      return {
        start: buildDateTimeValue(draft.absenceStartDate, draft.absenceAllDay ? "00:00" : draft.absenceStartTime, "00:00"),
        end: buildDateTimeValue(draft.absenceEndDate, draft.absenceAllDay ? "23:59:59" : draft.absenceEndTime, "23:59"),
      };
    }

    // Schichten zählen für die Verfügbarkeitsanzeige nur an ihrem Starttag
    // (gleiche Regel wie die Konflikt-Erkennung bei Übernacht-Schichten).
    const shiftStart = buildDateTimeValue(draft.startDate, draft.startTime, "00:00");
    return {
      start: shiftStart,
      end: clampShiftEndToStartDay(shiftStart, buildDateTimeValue(draft.endDate, draft.endTime, "23:59")),
    };
  }, [
    draft.absenceAllDay,
    draft.absenceEndDate,
    draft.absenceEndTime,
    draft.absenceStartDate,
    draft.absenceStartTime,
    draft.endDate,
    draft.endTime,
    draft.startDate,
    draft.startTime,
    isAbsence,
  ]);
  const availabilityByEmployeeId = useMemo(
    () => {
      if (!showEmployeeAvailability) return {};

      return employeeOptions.reduce((accumulator, employee) => {
        accumulator[employee.id] = getAvailabilitySummaryForRange({
          employeeId: employee.id,
          start: availabilityRange.start,
          end: availabilityRange.end,
          availabilityEntries,
          availabilityDefaults,
          companyAvailabilityMode,
          userAvailabilityModes,
        });
        return accumulator;
      }, {});
    },
    [
      availabilityDefaults,
      availabilityEntries,
      availabilityRange.end,
      availabilityRange.start,
      companyAvailabilityMode,
      employeeOptions,
      showEmployeeAvailability,
      userAvailabilityModes,
    ]
  );
  const visibleDepartmentOptions = useMemo(() => {
    const draftDepartment = draft.isCustomDepartment ? "" : asText(draft.department);
    const existingDepartment = departmentOptions.find(
      (department) => department.name.toLocaleLowerCase("de") === draftDepartment.toLocaleLowerCase("de")
    );

    return uniqueByName([
      ...departmentOptions,
      ...(draftDepartment
        ? [
            {
              id: existingDepartment?.id || draftDepartment,
              name: draftDepartment,
              shortName: existingDepartment?.shortName || "",
              color: existingDepartment?.color || "",
            },
          ]
        : []),
    ]);
  }, [departmentOptions, draft.department, draft.isCustomDepartment]);
  const visibleAbsenceOptions = useMemo(
    () =>
      uniqueById([
        ...normalizedAbsenceOptions,
        ...(draft.absenceType
          ? [
              {
                key: draft.absenceOptionKey || `${draft.absenceKind || ABSENCE_OPTION_SOURCES.ABSENCE}:${draft.absenceId || draft.userHolidayTypeId || draft.absenceType}`,
                id: draft.absenceId || draft.userHolidayTypeId || draft.absenceType,
                name: draft.absenceType,
                source: draft.absenceKind || ABSENCE_OPTION_SOURCES.ABSENCE,
              },
            ]
          : []),
      ]),
    [draft.absenceId, draft.absenceKind, draft.absenceOptionKey, draft.absenceType, draft.userHolidayTypeId, normalizedAbsenceOptions]
  );
  const selectedAbsence =
    visibleAbsenceOptions.find((option) => String(option.key) === String(draft.absenceOptionKey)) ||
    visibleAbsenceOptions.find((option) => String(option.id) === String(draft.absenceId || draft.userHolidayTypeId)) ||
    visibleAbsenceOptions.find(
      (option) => option.name.toLocaleLowerCase("de") === String(draft.absenceType || "").toLocaleLowerCase("de")
    );
  const absenceSelectValue = selectedAbsence ? selectedAbsence.key : EMPTY_OPTION_VALUE;
  const isHolidayAbsence = isAbsence && draft.absenceKind === ABSENCE_OPTION_SOURCES.USER_HOLIDAY;
  const absenceWorkingDayCount = useMemo(
    () => countWorkingDaysInRange(draft.absenceStartDate, draft.absenceEndDate, workingDaysPerWeek),
    [draft.absenceEndDate, draft.absenceStartDate, workingDaysPerWeek]
  );
  const createdMeta = buildCreatedMeta(isHolidayAbsence ? context?.userHoliday : context?.shift, isEditMode);
  const releaseMeta = isHolidayAbsence ? null : buildReleaseMeta(context?.shift);
  const renderAbsenceSelectValue = (selectedKey) => {
    const option = visibleAbsenceOptions.find((item) => String(item.key) === String(selectedKey));

    if (!option) {
      return <span className="spv2-editor-select-placeholder">Absenzart wählen</span>;
    }

    const isUserHoliday = option.source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY;

    return (
      <span className={`spv2-editor-absence-value${isUserHoliday ? " is-user-holiday" : ""}`}>
        <span className="spv2-editor-absence-value-name">{option.name}</span>
        <AbsenceSourceBadge source={option.source} className="spv2-editor-absence-value-badge" />
      </span>
    );
  };
  const editorTitle = isAbsence
    ? isEditMode
      ? isHolidayAbsence
        ? "Ferien bearbeiten"
        : "Absenz bearbeiten"
      : isHolidayAbsence
        ? "Ferien erfassen"
        : "Absenz erfassen"
    : isEditMode
      ? "Schicht bearbeiten"
      : "Schicht erstellen";
  const saveLabel = "Speichern";
  const canReleaseShift = !isAbsence;
  const deletableEntry = context?.shift || context?.userHoliday || null;
  const canDeleteEntry = isEditMode && Boolean(deletableEntry);
  const isEditingPersistedAbsence =
    isEditMode && (Boolean(context?.userHoliday) || context?.shift?.entryType === ENTRY_TYPES.ABSENCE);
  const entryTypeOptions = isEditingPersistedAbsence
    ? [
        {
          type: ENTRY_TYPES.ABSENCE,
          icon: <EventBusyOutlinedIcon fontSize="small" />,
          label: "Absenz",
        },
      ]
    : [
        {
          type: ENTRY_TYPES.SHIFT,
          icon: <WorkOutlineOutlinedIcon fontSize="small" />,
          label: "Schicht",
        },
        {
          type: ENTRY_TYPES.ABSENCE,
          icon: <EventBusyOutlinedIcon fontSize="small" />,
          label: "Absenz",
        },
      ];
  const HeaderIcon = isAbsence
    ? isHolidayAbsence
      ? IconHoliday
      : EventBusyOutlinedIcon
    : WorkOutlineOutlinedIcon;
  // Der Ton des Signets trennt Schicht, Absenz und Ferien – dieselben Farben,
  // die die Einträge auch im Raster tragen.
  const headerVisualTone = isAbsence ? (isHolidayAbsence ? "holiday" : "absence") : "shift";
  const headerSubtitle = isAbsence
    ? buildHeaderSubtitle({ startDate: draft.absenceStartDate, endDate: draft.absenceEndDate })
    : buildHeaderSubtitle({
        startDate: draft.startDate,
        endDate: draft.endDate,
        department: draft.department,
      });
  const shiftDurationLabel = isAbsence ? "" : formatDurationLabel(getShiftDurationMinutes(draft));

  const updateDraft = (patch) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const updateRange = (keys, patch) => {
    setDraft((current) => normalizeDateTimeRange(current, keys, patch));
  };

  const handleEntryTypeChange = (entryType) => {
    if (isEditingPersistedAbsence && entryType !== ENTRY_TYPES.ABSENCE) return;

    if (entryType !== ENTRY_TYPES.ABSENCE) {
      updateDraft({ entryType });
      return;
    }

    const nextAbsence =
      visibleAbsenceOptions.find((option) => String(option.id) === String(draft.absenceId)) ||
      visibleAbsenceOptions[0] ||
      null;

    updateDraft({
      entryType,
      ...absenceDraftPatch(nextAbsence),
    });
  };

  const handleAbsenceChange = (event) => {
    const nextId = event.target.value === EMPTY_OPTION_VALUE ? "" : event.target.value;
    const nextAbsence = visibleAbsenceOptions.find((option) => String(option.key) === String(nextId));

    updateDraft({
      ...absenceDraftPatch(nextAbsence),
    });
  };

  const handleDepartmentSelect = (option) => {
    if (option.kind === "new-custom") {
      // Leeres Feld, damit direkt getippt werden kann; leer gespeichert
      // bedeutet "keine Abteilung" (siehe buildEditorEventPayload).
      setDraft((current) => ({ ...current, department: "", isCustomDepartment: true }));
      return;
    }

    if (option.kind === "custom") {
      setDraft((current) => ({ ...current, department: option.name, isCustomDepartment: true }));
      return;
    }

    applyDepartment(option.name);
  };

  const handleCustomDepartmentInput = (nextValue) => {
    setDraft((current) => ({ ...current, department: nextValue, isCustomDepartment: true }));
  };

  const applyDepartment = (nextDepartment) => {
    setDraft((current) => {
      if (isCustomTemplateId(current.templateId)) {
        return { ...current, department: nextDepartment, isCustomDepartment: false };
      }

      const availableTemplates = templateOptions.filter((template) => templateMatchesDepartment(template, nextDepartment));
      const currentTemplate = availableTemplates.find((template) => String(template.id) === String(current.templateId));

      if (currentTemplate) {
        return { ...current, department: nextDepartment, isCustomDepartment: false };
      }

      const nextTemplate = availableTemplates[0] || null;
      const times = parseTimeRange(nextTemplate?.timeLabel);
      const nextStartTime = times.startTime || current.startTime;
      const nextEndTime = times.endTime || current.endTime;

      return normalizeDateTimeRange(current, SHIFT_RANGE_KEYS, {
        department: nextDepartment,
        isCustomDepartment: false,
        templateId: nextTemplate?.id || CUSTOM_SHIFT_TEMPLATE_ID,
        title: nextTemplate?.name || "",
        startTime: nextStartTime,
        endTime: nextEndTime,
        endDate: getTemplateEndDate(current.startDate, nextStartTime, nextEndTime),
        // Der Abteilungswechsel waehlt eine neue Vorlage — deren Pausen
        // gehoeren wie die Zeiten dazu (gleiche Regel wie applyTemplate).
        breaks: templateBreaksToDraft(nextTemplate),
      });
    });
  };

  const applyTemplate = (templateId) => {
    if (isCustomTemplateId(templateId)) {
      setDraft((current) => ({
        ...current,
        templateId: CUSTOM_SHIFT_TEMPLATE_ID,
        title: isCustomTemplateId(current.templateId) ? current.title : "",
      }));
      return;
    }

    const template = filteredTemplateOptions.find((item) => String(item.id) === String(templateId));
    const times = parseTimeRange(template?.timeLabel);

    setDraft((current) => {
      if (!template) {
        return { ...current, templateId: "", title: "" };
      }

      const nextStartTime = times.startTime || current.startTime;
      const nextEndTime = times.endTime || current.endTime;

      return normalizeDateTimeRange(current, SHIFT_RANGE_KEYS, {
        templateId,
        title: template?.name || current.title,
        // Eine individuelle Abteilung überschreibt die Vorlage nicht: sie wurde
        // ausdrücklich gesetzt, die Vorlage liefert nur Zeiten und Namen.
        department: current.isCustomDepartment
          ? current.department
          : template?.department || current.department,
        startTime: nextStartTime,
        endTime: nextEndTime,
        endDate: getTemplateEndDate(current.startDate, nextStartTime, nextEndTime),
        // Wie die Zeiten gehören auch die Pausen zur Vorlage.
        breaks: templateBreaksToDraft(template),
      });
    });
  };

  const handleTemplateChange = (nextValue) => {
    if (typeof nextValue === "string") {
      setDraft((current) => ({
        ...current,
        templateId: CUSTOM_SHIFT_TEMPLATE_ID,
        title: nextValue,
      }));
      return;
    }

    if (!nextValue || nextValue.isCustom) {
      applyTemplate(CUSTOM_SHIFT_TEMPLATE_ID);
      return;
    }

    applyTemplate(nextValue.id);
  };

  const handleTemplateInputChange = (nextInputValue, reason) => {
    if (reason !== "input") return;

    setDraft((current) => ({
      ...current,
      templateId: CUSTOM_SHIFT_TEMPLATE_ID,
      title: nextInputValue,
    }));
  };

  const addBreak = () => {
    setDraft((current) => ({
      ...current,
      breaks: [
        ...current.breaks,
        {
          id: `break-${Date.now()}`,
          type: "fixed",
          startTime: "12:00",
          endTime: "12:30",
          durationMinutes: null,
        },
      ],
    }));
  };

  const updateBreak = (breakId, patch) => {
    setDraft((current) => ({
      ...current,
      breaks: current.breaks.map((item) => {
        if (item.id !== breakId) return item;
        const nextBreak = { ...item, ...patch };
        if (isTimeBefore(nextBreak.endTime, nextBreak.startTime)) {
          nextBreak.endTime = nextBreak.startTime;
        }
        return nextBreak;
      }),
    }));
  };

  const removeBreak = (breakId) => {
    setDraft((current) => ({
      ...current,
      breaks: current.breaks.filter((item) => item.id !== breakId),
    }));
  };

  const submitDraft = (releaseAfterSave) => {
    onSubmit?.({ context, draft, releaseAfterSave });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitDraft(false);
  };

  const handleDelete = () => {
    if (!canDeleteEntry) return;
    onDelete?.(deletableEntry);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className="spv2-shift-editor-root"
      BackdropProps={{ className: "spv2-shift-editor-backdrop" }}
      PaperProps={{ className: "spv2-shift-editor-paper" }}
    >
      <Box component="form" className="spv2-shift-editor" onSubmit={handleSubmit}>
        {/* Kopf, Fuss und Schliessen-Knopf sind die DS-Dialogklassen (.ds-dialog__*). */}
        <Box className="ds-dialog__header spv2-editor-header">
          {/*
            Das Signet steht neben dem Textblock, nicht in der Titelzeile wie im
            DS: dort trägt das Feld ein 16px-Icon, das die Zeile nicht höher
            macht. Ein 36px-Signet darin würde die Zeile aufblähen und den
            Untertitel nach unten schieben – so bleibt er dicht am Titel.
          */}
          <Box
            className={`ds-dialog__header-visual spv2-editor-title-icon is-${headerVisualTone}`}
            aria-hidden="true"
          >
            <HeaderIcon fontSize="small" />
          </Box>
          <Box className="ds-dialog__heading">
            {/* Schlichte Elemente statt MUI-Typography: so greifen die
                DS-Klassen ohne Spezifitätskampf mit den MUI-Grundstilen. */}
            <h3 className="ds-dialog__title">{editorTitle}</h3>
            {headerSubtitle ? <p className="ds-dialog__subtitle">{headerSubtitle}</p> : null}
          </Box>
          <Box className="ds-dialog__controls">
            {canDeleteEntry ? (
              <button
                type="button"
                className="ds-dialog__close spv2-editor-delete"
                aria-label="Eintrag löschen"
                onClick={handleDelete}
              >
                <IconDelete sx={{ fontSize: 18 }} />
              </button>
            ) : null}
            <button type="button" className="ds-dialog__close" aria-label="Dialog schliessen" onClick={onClose}>
              <IconClose sx={{ fontSize: 18 }} />
            </button>
          </Box>
        </Box>

        <Box className="spv2-editor-body">
          <Box
            className={`spv2-editor-segments${isEditingPersistedAbsence ? " is-single-option" : ""}`}
            role="tablist"
            aria-label="Eintragstyp"
          >
            {entryTypeOptions.map((option) => (
              <SegmentButton
                key={option.type}
                active={draft.entryType === option.type}
                icon={option.icon}
                onClick={() => handleEntryTypeChange(option.type)}
              >
                {option.label}
              </SegmentButton>
            ))}
          </Box>

          <Box className="spv2-editor-grid spv2-editor-assignment-grid">
            <Field label="Mitarbeitende" icon={<PersonOutlineOutlinedIcon fontSize="inherit" />} span={2}>
              <EmployeeSelect
                employees={employeeOptions}
                value={selectedEmployee}
                disabled={!employeeOptions.length}
                showAvailability={showEmployeeAvailability}
                availabilityByEmployeeId={availabilityByEmployeeId}
                onChange={(employee) =>
                  updateDraft({
                    employeeId: employee?.id || "",
                    employeeName: employee?.name || "",
                  })
                }
              />
            </Field>
          </Box>

          {!isAbsence ? (
            <>
              <Box className="spv2-editor-grid">
                <Field label="Abteilung" icon={<IconDepartment fontSize="inherit" />}>
                  <DepartmentSelect
                    departments={visibleDepartmentOptions}
                    value={draft.department || ""}
                    isCustom={Boolean(draft.isCustomDepartment)}
                    onSelect={handleDepartmentSelect}
                    onInputChange={handleCustomDepartmentInput}
                  />
                </Field>

                <Field label="Schicht" icon={<WorkOutlineOutlinedIcon fontSize="inherit" />}>
                  <ShiftTemplateSelect
                    templates={filteredTemplateOptions}
                    value={selectedTemplateValue}
                    inputValue={templateInputValue}
                    onChange={handleTemplateChange}
                    onInputChange={handleTemplateInputChange}
                  />
                </Field>

                {/* Sichtbar "Beginn"/"Ende"; die Vorlesehilfe der Felder bleibt
                    genauer, weil Datum und Uhrzeit getrennt bedient werden. */}
                <DateTimeField
                  label="Beginn"
                  dateValue={draft.startDate}
                  timeValue={draft.startTime}
                  dateAriaLabel="Startdatum"
                  timeAriaLabel="Startzeit"
                  onDateChange={(event) => updateRange(SHIFT_RANGE_KEYS, { startDate: event.target.value })}
                  onTimeCommit={(startTime) => updateRange(SHIFT_RANGE_KEYS, { startTime })}
                />
                <DateTimeField
                  label="Ende"
                  dateValue={draft.endDate}
                  timeValue={draft.endTime}
                  minDate={draft.startDate || undefined}
                  minTime={isSameDate(draft.startDate, draft.endDate) ? draft.startTime || undefined : undefined}
                  dateAriaLabel="Enddatum"
                  timeAriaLabel="Endzeit"
                  onDateChange={(event) => updateRange(SHIFT_RANGE_KEYS, { endDate: event.target.value })}
                  onTimeCommit={(endTime) => updateRange(SHIFT_RANGE_KEYS, { endTime })}
                />
              </Box>

              <Box className="spv2-editor-breaks">
                {draft.breaks.map((item) =>
                  item.type === "duration" ? (
                    /* Dauer-Pause aus den Schicht-Stammdaten: ohne Lage im
                       Tag, nur die Minutenzahl zaehlt von der Zeit weg. */
                    <Box key={item.id} className="spv2-editor-break-row spv2-editor-break-row--duration">
                      <input
                        className="spv2-editor-control spv2-editor-break-minutes"
                        inputMode="numeric"
                        value={item.durationMinutes ?? ""}
                        aria-label="Pause in Minuten"
                        onChange={(event) => {
                          const minutes = Number(event.target.value.replace(/[^\d]/g, "").slice(0, 3));
                          updateBreak(item.id, { durationMinutes: minutes > 0 ? minutes : null });
                        }}
                      />
                      <span>Min Pause</span>
                      <IconButton className="spv2-editor-break-remove" size="small" aria-label="Pause entfernen" onClick={() => removeBreak(item.id)}>
                        <IconDelete fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                  <Box key={item.id} className="spv2-editor-break-row">
                    <TimeTextInput
                      className="spv2-editor-control"
                      value={item.startTime}
                      ariaLabel="Pause von"
                      onCommit={(startTime) => updateBreak(item.id, { startTime })}
                    />
                    <span>bis</span>
                    <TimeTextInput
                      className="spv2-editor-control"
                      value={item.endTime}
                      min={item.startTime || undefined}
                      ariaLabel="Pause bis"
                      onCommit={(endTime) => updateBreak(item.id, { endTime })}
                    />
                    <IconButton className="spv2-editor-break-remove" size="small" aria-label="Pause entfernen" onClick={() => removeBreak(item.id)}>
                      <IconDelete fontSize="small" />
                    </IconButton>
                  </Box>
                  )
                )}
                {/* Pause anlegen links, die daraus folgende Dauer rechts. */}
                <Box className="spv2-editor-breaks-footer">
                  <button type="button" className="spv2-editor-add-break" onClick={addBreak}>
                    <IconAdd fontSize="small" />
                    <span>{draft.breaks.length ? "Weitere Pause" : "Pause hinzufügen"}</span>
                  </button>
                  {shiftDurationLabel ? (
                    <span className="spv2-editor-duration">
                      <span className="spv2-editor-duration-label">Dauer:</span>
                      <span className="spv2-editor-duration-value">{shiftDurationLabel}</span>
                    </span>
                  ) : null}
                </Box>
              </Box>

              {/* Die Notiz landet über /api/shiftplan als description beim
                  Eintrag und ist damit in der App sichtbar - das muss beim
                  Schreiben klar sein, darum steht es schon im Label. */}
              <Field
                label="Notiz für Mitarbeitende"
                icon={<IconEdit fontSize="inherit" />}
                span={2}
                className="spv2-editor-note-field"
              >
                <textarea
                  className="spv2-editor-control spv2-editor-textarea"
                  value={draft.note}
                  placeholder="Wird in der App angezeigt …"
                  onChange={(event) => updateDraft({ note: event.target.value })}
                />
              </Field>
            </>
          ) : (
            <>
              <Box className="spv2-editor-grid">
                <Field label="Absenzart" icon={<EventBusyOutlinedIcon fontSize="inherit" />} span={2}>
                  <EditorSelect
                    ariaLabel="Absenzart"
                    value={absenceSelectValue}
                    disabled={!visibleAbsenceOptions.length}
                    renderValue={renderAbsenceSelectValue}
                    onChange={handleAbsenceChange}
                  >
                    <MenuItem value={EMPTY_OPTION_VALUE} disabled>
                      {visibleAbsenceOptions.length ? "Absenzart wählen" : "Keine Absenzarten"}
                    </MenuItem>
                    {visibleAbsenceOptions.map((option) => (
                      <MenuItem
                        key={option.key || option.id}
                        value={option.key}
                        className={`spv2-editor-absence-option${
                          option.source === ABSENCE_OPTION_SOURCES.USER_HOLIDAY ? " is-user-holiday" : ""
                        }`}
                      >
                        <span className="spv2-editor-absence-option-name">{option.name}</span>
                        <AbsenceSourceBadge source={option.source} className="spv2-editor-absence-option-badge" />
                      </MenuItem>
                    ))}
                  </EditorSelect>
                </Field>
                <Field label="Startdatum" icon={<CalendarMonthOutlinedIcon fontSize="inherit" />}>
                  <input
                    type="date"
                    className="spv2-editor-control"
                    value={draft.absenceStartDate}
                    onChange={(event) => updateRange(ABSENCE_RANGE_KEYS, { absenceStartDate: event.target.value })}
                  />
                </Field>
                <Field label="Enddatum" icon={<CalendarMonthOutlinedIcon fontSize="inherit" />}>
                  <input
                    type="date"
                    className="spv2-editor-control"
                    value={draft.absenceEndDate}
                    min={draft.absenceStartDate || undefined}
                    onChange={(event) => updateRange(ABSENCE_RANGE_KEYS, { absenceEndDate: event.target.value })}
                  />
                </Field>
                {!isHolidayAbsence ? (
                  <Box className="spv2-editor-toggle-field span-2">
                    <span>Ganztägig</span>
                    <button
                      type="button"
                      className={`spv2-editor-toggle${draft.absenceAllDay ? " is-on" : ""}`}
                      aria-pressed={draft.absenceAllDay}
                      aria-label="Ganztägig umschalten"
                      onClick={() => updateDraft({ absenceAllDay: !draft.absenceAllDay })}
                    />
                  </Box>
                ) : null}
                {!isHolidayAbsence && !draft.absenceAllDay ? (
                  <>
                    <TimeField
                      label="Von"
                      value={draft.absenceStartTime}
                      ariaLabel="Absenz von"
                      onCommit={(absenceStartTime) => updateRange(ABSENCE_RANGE_KEYS, { absenceStartTime })}
                    />
                    <TimeField
                      label="Bis"
                      value={draft.absenceEndTime}
                      min={
                        isSameDate(draft.absenceStartDate, draft.absenceEndDate)
                          ? draft.absenceStartTime || undefined
                          : undefined
                      }
                      ariaLabel="Absenz bis"
                      onCommit={(absenceEndTime) => updateRange(ABSENCE_RANGE_KEYS, { absenceEndTime })}
                    />
                  </>
                ) : null}

                <AbsenceHoursField
                  span={2}
                  value={draft.hoursPerDay}
                  typeName={selectedAbsence?.name || draft.absenceType}
                  typeHours={selectedAbsence?.hoursPerDay ?? null}
                  countsAsWorkTime={selectedAbsence?.countsAsWorkTime !== false}
                  isAllDay={Boolean(draft.absenceAllDay)}
                  startTime={draft.absenceStartTime}
                  endTime={draft.absenceEndTime}
                  workingDayCount={absenceWorkingDayCount}
                  onChange={(hoursPerDay) => updateDraft({ hoursPerDay })}
                  onReset={() => updateDraft({ hoursPerDay: hoursToField(selectedAbsence?.hoursPerDay) })}
                />
              </Box>

              {!isHolidayAbsence ? (
                <Field
                  label="Notiz für Mitarbeitende"
                  icon={<IconEdit fontSize="inherit" />}
                  span={2}
                  className="spv2-editor-note-field"
                >
                  <textarea
                    className="spv2-editor-control spv2-editor-textarea"
                    value={draft.absenceNote}
                    placeholder="Wird in der App angezeigt …"
                    onChange={(event) => updateDraft({ absenceNote: event.target.value })}
                  />
                </Field>
              ) : null}
            </>
          )}
        </Box>

        <Box className="ds-dialog__footer spv2-editor-footer">
          <Box className="ds-dialog__footer-start spv2-editor-footer-meta">
            <FooterMetaRow meta={createdMeta} />
            <FooterMetaRow meta={releaseMeta} />
          </Box>
          <Box className="ds-dialog__footer-actions">
            {/*
              Freigeben nur per Klick, nie per Enter: als erster Submit-Button
              im Formular waere dieser Knopf sonst der Default-Submitter der
              impliziten Formular-Submission — Enter im Namensfeld einer
              individuellen Abteilung wuerde die Schicht still veroeffentlichen.
            */}
            {canReleaseShift ? (
              <AppButton type="button" variant="neutral" onClick={() => submitDraft(true)}>
                Speichern & freigeben
              </AppButton>
            ) : null}
            <AppButton type="submit" variant="primary">
              {saveLabel}
            </AppButton>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
